// How much a video jump may exceed real time before it counts as a seek.
// At 1x the video advances as fast as the clock; the slack absorbs the
// player's own jitter between two checkpoints.
export const SEEK_TOLERANCE_SECONDS = 1;

// Real time a span left unused, carried to the next span. Date.now() is whole
// milliseconds and the media clock jitters, so a span's video advance is
// sometimes a hair above its wall time and sometimes below. Taking the
// minimum of each span on its own lost the difference every time -- about
// 1 s per 10 minutes, enough to leave a full watch of a long clip at 99%.
// Capped so credit can never run more than this far ahead of real time.
export const MAX_SLACK_SECONDS = 1;

/**
 * Learn-time meter: credits what the video actually played.
 *
 * The classroom used to credit one second per setInterval tick. Browsers
 * throttle timers in a page that is not active -- Chrome wakes a hidden,
 * silent tab once a minute -- while the video keeps playing, so a student
 * who played 417 s with the tab hidden was credited 44 s. Every checkpoint
 * here credits min(wall-clock gap + carried slack, video advance) since the
 * previous one (see MAX_SLACK_SECONDS), so
 * a late checkpoint credits the whole gap, a seek credits nothing, and the
 * fraction of a second already played is kept rather than dropped on pause.
 *
 * Only a span whose start was countable is credited: a span that began
 * paused, seeking or at another rate credits nothing.
 *
 * @param {{now: function(): number, read: function(): {position: number, countable: boolean}}} options
 *   now: wall clock in milliseconds; read: the player's position in seconds
 *   and whether it is playing at 1x right now.
 * @return {{checkpoint: function(): number, finish: function(): number, reset: function(): void}}
 *   checkpoint and finish return the whole seconds credited by that call.
 */
export function createLearnTimeMeter({ now, read }) {
  let active = false;
  let lastWall = 0;
  let lastPosition = 0;
  let pending = 0;
  let slack = 0;

  function anchor(wall, playback) {
    lastWall = wall;
    lastPosition = playback.position;
    active = playback.countable;
  }

  return {
    checkpoint() {
      const wall = now();
      const playback = read();
      if (active) {
        const wallSeconds = (wall - lastWall) / 1000;
        const playedSeconds = playback.position - lastPosition;
        if (playedSeconds > 0 && playedSeconds <= wallSeconds + SEEK_TOLERANCE_SECONDS) {
          const available = slack + wallSeconds;
          const credit = Math.min(available, playedSeconds);
          pending += credit;
          slack = Math.min(MAX_SLACK_SECONDS, available - credit);
        } else {
          slack = 0;
        }
      } else {
        slack = 0;
      }
      anchor(wall, playback);
      const whole = Math.floor(pending);
      pending -= whole;
      return whole;
    },
    // The video played to its end: credit the fraction still pending,
    // rounded up. Under a second, and it covers the moment between pressing
    // play and the first checkpoint, which no span ever measures.
    finish() {
      const whole = Math.ceil(pending);
      pending = 0;
      return whole;
    },
    // A new source or a new player: forget the fraction and start from here.
    reset() {
      pending = 0;
      slack = 0;
      anchor(now(), read());
    },
  };
}

/**
 * Add credited seconds to a topic's learn time, capped at the video length.
 * Complete is exactly 100%: learnTime has to reach vdoLength.
 *
 * @param {number} learnTime current learn time in seconds
 * @param {number} vdoLength video length in seconds
 * @param {number} seconds seconds to add
 * @return {{learnTime: number, percent: number}}
 */
export function creditLearnTime(learnTime, vdoLength, seconds) {
  let newLearnTime = learnTime + seconds;
  if (newLearnTime > vdoLength) {
    newLearnTime = vdoLength;
  }
  let percent = Math.floor((newLearnTime / vdoLength) * 100);
  if (percent > 100) {
    percent = 100;
  }
  return { learnTime: newLearnTime, percent };
}
